import { useEffect } from 'react';
import { STATUSES } from '../plan/arc';
import type { Arc, Status } from '../plan/arc';

/**
 * The board's keyboard.
 *
 * Only ever bound at the window, and only while nothing is being typed into —
 * a shortcut that fires while someone is writing a log entry is worse than no
 * shortcut at all. `contentEditable` counts, since a rich composer is where
 * this would bite hardest.
 */
export interface BoardKeyActions {
  /** Top-level arcs in the order they render, which is the order focus moves. */
  rows: Arc[];
  focused: string | null;
  setFocused: (name: string | null) => void;
  onStatus: (arc: Arc, status: Status) => void;
  onNudge: (arc: Arc, direction: -1 | 1) => void;
  onOpen: (arc: Arc) => void;
  /** Given first refusal on esc — a selection is louder than a focus ring,
   *  so it is what someone means to dismiss. */
  onEscape?: () => void;
  enabled: boolean;
}

export function useBoardKeys({
  rows,
  focused,
  setFocused,
  onStatus,
  onNudge,
  onOpen,
  onEscape,
  enabled,
}: BoardKeyActions): void {
  useEffect(() => {
    if (!enabled) return;

    const onKey = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;
      if (event.metaKey || event.ctrlKey) return;

      if (event.key === 'Escape') {
        if (onEscape) onEscape();
        setFocused(null);
        return;
      }

      const index = rows.findIndex((a) => a.name === focused);
      const current = index >= 0 ? rows[index] : undefined;

      // j/k move focus, and start it at the top when nothing is focused —
      // the first press should do something visible rather than nothing.
      if (event.key === 'j' || event.key === 'k') {
        event.preventDefault();
        const step = event.key === 'j' ? 1 : -1;
        const next = index < 0 ? (step > 0 ? 0 : rows.length - 1) : clamp(index + step, rows.length);
        setFocused(rows[next]?.name ?? null);
        return;
      }

      if (!current) return;



      if (event.key === 'Enter') {
        event.preventDefault();
        onOpen(current);
        return;
      }

      // ⌥↑ / ⌥↓ reorder; bare arrows are left to the page so scrolling still
      // works with a row focused.
      if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();
        onNudge(current, event.key === 'ArrowUp' ? -1 : 1);
        return;
      }

      const digit = Number(event.key);
      if (digit >= 1 && digit <= STATUSES.length) {
        event.preventDefault();
        onStatus(current, STATUSES[digit - 1]!);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, focused, setFocused, onStatus, onNudge, onOpen, onEscape, enabled]);
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

function clamp(index: number, length: number): number {
  return Math.max(0, Math.min(index, length - 1));
}
